import { useTranslation } from "react-i18next";
import { CircleCheckIcon, InfoIcon } from "lucide-react";
import { formatDateRange, formatMoney, formatMoneyCompact, splitPool, type LeaderboardResponse } from "@scandi/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { OUTPUT_TONE, SPEND_TONE } from "@/components/leaderboard/bonus-tones";
import type { Translate } from "@/i18n/translate";

/**
 * Compact period header. Its main job is the deadline: how long is left to
 * change where you land before the money is decided. Under it, one line says
 * what the money is and how it divides - the whole explanation of the split,
 * with the counting rule a hover away rather than a block of copy.
 */
export function PeriodBar({ data }: { data: LeaderboardResponse }) {
  const { t } = useTranslation();
  const { period, progress } = data;

  if (!period || !progress) return <CustomRangeBar data={data} />;

  const finished = progress.isComplete || !period.isCurrent;

  return (
    <Card className="overflow-hidden py-0">
      <CardContent className="flex flex-col gap-2 px-4 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-semibold">
            {finished ? (
              <span className="text-muted-foreground flex items-center gap-1.5">
                <CircleCheckIcon className="size-3.5" />
                {t("leaderboard.periodClosed")}
              </span>
            ) : (
              <>
                <span className="text-success">{countdown(progress.remaining, t)}</span>
                <span className="text-muted-foreground"> {t("leaderboard.untilPayment")}</span>
              </>
            )}
          </span>
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {formatDateRange(period.start, period.end)}
          </span>
        </div>

        <Progress value={progress.fraction * 100} className="h-1.5" />

        <PoolLine data={data} />
      </CardContent>
    </Card>
  );
}

/** "€1,000 pool · 50% by spend · 50% by videos launched (i)". */
function PoolLine({ data }: { data: LeaderboardResponse }) {
  const { t } = useTranslation();
  const { terms, currency, team } = data;
  const isSplit = terms.mode === "split";
  // As the server pays it: a side with nothing to divide by hands its half to the other.
  const parts = splitPool(data.pool, terms.spendWeight, team);
  const pct = (n: number) => `${Math.round((n / (data.pool || 1)) * 100)}%`;

  return (
    <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
      <span className="text-foreground font-semibold tabular-nums">{t("bonus.pool.amount", { amount: formatMoney(data.pool, currency) })}</span>
      {isSplit ? (
        <>
          <span aria-hidden>·</span>
          <span className="flex items-center gap-1">
            <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: SPEND_TONE }} />
            {t("bonus.pool.bySpend", { pct: pct(parts.spend) })}
          </span>
          <span aria-hidden>·</span>
          <span className="flex items-center gap-1">
            <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: OUTPUT_TONE }} />
            {t("bonus.pool.byVideos", { context: terms.outputUnit, pct: pct(parts.output) })}
          </span>
          <Tooltip>
            <TooltipTrigger aria-label={t("bonus.pool.howLabel")} className="hover:text-foreground cursor-help">
              <InfoIcon className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="max-w-72">
              {t("bonus.pool.how", {
                context: terms.outputUnit,
                spend: formatMoneyCompact(parts.spend, currency),
                videos: formatMoneyCompact(parts.output, currency),
              })}
              {terms.outputSpendFloor > 0 && ` ${t("bonus.pool.floorRule", { context: terms.outputUnit, amount: formatMoney(terms.outputSpendFloor, currency) })}`}
              {terms.outputConceptCap !== null && terms.outputUnit === "variant" && ` ${t("bonus.pool.capRule", { count: terms.outputConceptCap })}`}
            </TooltipContent>
          </Tooltip>
        </>
      ) : (
        <>
          <span aria-hidden>·</span>
          <span>{t("bonus.pool.metric", { context: terms.metric })}</span>
        </>
      )}
    </p>
  );
}

function countdown(remaining: number, t: Translate): string {
  if (remaining === 0) return t("leaderboard.finalDay");
  return t("leaderboard.daysLeft", { count: remaining });
}

/** Arbitrary ranges have no deadline, so they only state the window. */
function CustomRangeBar({ data }: { data: LeaderboardResponse }) {
  const { t } = useTranslation();
  const days =
    Math.round((Date.parse(data.range.to) - Date.parse(data.range.from)) / 86_400_000) + 1;

  return (
    <Card className="overflow-hidden py-0">
      <CardContent className="flex items-baseline justify-between gap-3 px-4 py-3">
        <span className="text-sm font-semibold">
          {formatDateRange(data.range.from, data.range.to)}
        </span>
        <span className="text-muted-foreground text-xs">
          {t("leaderboard.rangeDays", { count: days })}
        </span>
      </CardContent>
    </Card>
  );
}
